import { Box, ButtonGroup } from "@mui/material"
import { useLocation } from "@tanstack/react-router"
import type { FC } from "react"
import { useMemo } from "react"
import { twMerge } from "tailwind-merge"

import { LinkButton } from "@/components/common/LinkButton"
import { PermissionActionType } from "@/constants/permissions"
import { usePermissionManager } from "@/hooks/usePermissionManager"

interface DashboardTab {
  label: string
  path: string
  testId: string
}

export const DashboardTabs: FC = () => {
  const location = useLocation()
  const { hasPermissions } = usePermissionManager()
  const canViewPendingApprovals = hasPermissions([PermissionActionType.DASHBOARD_PENDING_APPROVALS])
  const canViewMyRequests = hasPermissions([PermissionActionType.DASHBOARD_MY_REQUESTS])

  const tabs = useMemo<DashboardTab[]>(
    () => [
      ...(canViewPendingApprovals
        ? [
            {
              label: "Pending Approvals",
              path: "/app/dashboard/pending-approvals",
              testId: "dashboard.tabs.pending-approvals-tab",
            },
          ]
        : []),
      ...(canViewMyRequests
        ? [
            {
              label: "My Requests",
              path: "/app/dashboard/my-requests",
              testId: "dashboard.tabs.my-requests-tab",
            },
          ]
        : []),
    ],
    [canViewPendingApprovals, canViewMyRequests]
  )

  return (
    <Box>
      <ButtonGroup
        color='inherit'
        orientation='horizontal'
        variant='outlined'
        className='border border-gray-400 bg-white'
      >
        {tabs.map((tab) => {
          const isActive = location.pathname.startsWith(tab.path)
          return (
            <LinkButton
              key={tab.path}
              appearance='text'
              to={tab.path}
              className={twMerge(
                "h-9 rounded-none border-l border-gray-400 uppercase text-gray-700 first:rounded-l first:border-l-0 last:rounded-r",
                isActive && "bg-gray-300"
              )}
              data-testid={tab.testId}
            >
              {tab.label}
            </LinkButton>
          )
        })}
      </ButtonGroup>
    </Box>
  )
}
